import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ReducersTypes } from "@/redux/reducers/Models";
import {
  getVehicles,
  setCustomer,
  deleteVehicle,
} from "@/redux/ducks/customer";
import { Vehicle } from "../Models/index";

export function useVehicles() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const customer = useSelector((state: ReducersTypes) => state.customerReducer);

  const [searchPlate, setSearchPlate] = useState("");
  const [isShowingMenu, setIsShowingMenu] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState<Vehicle | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [isVisibleTryDeleteModal, setIsVisibleTryDeleteModal] = useState(false);

  useEffect(() => {
    dispatch(getVehicles());
  }, [dispatch]);

  const vehicles = useMemo(
    () =>
      (customer?.vehicles || []).map((vehicle) => ({
        ...vehicle,
        brand: vehicle.brand || "",
        model: vehicle.model || "",
        plate: vehicle.plate || "",
      })) as Vehicle[],
    [customer?.vehicles],
  );

  // Ensure at least one vehicle is selected when vehicles are loaded
  useEffect(() => {
    if (
      vehicles.length > 0 &&
      (!customer?.selectedVehicles || customer.selectedVehicles.length === 0)
    ) {
      dispatch(setCustomer({ selectedVehicles: [vehicles[0].plate] }));
    }
  }, [vehicles, customer?.selectedVehicles, dispatch]);

  const filteredVehicles = useMemo(
    () =>
      vehicles.filter((vehicle) => {
        const searchTerm = searchPlate.toLowerCase();
        return (
          vehicle.plate.toLowerCase().includes(searchTerm) ||
          vehicle.brand.toLowerCase().includes(searchTerm) ||
          vehicle.model.toLowerCase().includes(searchTerm)
        );
      }),
    [vehicles, searchPlate],
  );

  const isAllSelected = useMemo(
    () =>
      filteredVehicles.length > 0 &&
      filteredVehicles.every((vehicle) =>
        customer?.selectedVehicles?.includes(vehicle.plate),
      ),
    [filteredVehicles, customer?.selectedVehicles],
  );

  const toggleMenu = useCallback(() => setIsShowingMenu((prev) => !prev), []);

  const goBack = useCallback(() => {
    navigate("..", { relative: "path" });
  }, [navigate]);

  const goToBuy = useCallback(() => navigate("/buy"), [navigate]);

  const toggleVehicleSelection = useCallback(
    (plate: string) => {
      const currentSelected = customer?.selectedVehicles || [];
      // Prevent deselection if it's the last selected vehicle
      if (currentSelected.includes(plate) && currentSelected.length === 1) {
        return;
      }
      const newSelected = currentSelected.includes(plate)
        ? currentSelected.filter((p) => p !== plate)
        : [...currentSelected, plate];
      dispatch(setCustomer({ selectedVehicles: newSelected }));
    },
    [customer?.selectedVehicles, dispatch],
  );

  const toggleSelectAll = useCallback(() => {
    if (isAllSelected) {
      // When deselecting all, keep the first vehicle selected
      dispatch(setCustomer({ selectedVehicles: [filteredVehicles[0].plate] }));
    } else {
      dispatch(
        setCustomer({ selectedVehicles: filteredVehicles.map((v) => v.plate) }),
      );
    }
  }, [filteredVehicles, isAllSelected, dispatch]);

  const handleDeleteVehicle = useCallback(
    (success: boolean) => {
      setIsLoading(false);
      dispatch(getVehicles());
      if (!success) {
        setError(true);
      } else if (vehicleToDelete) {
        const currentSelected = customer?.selectedVehicles || [];
        const newSelected = currentSelected.filter(
          (plate) => plate !== vehicleToDelete.plate,
        );

        if (newSelected.length === 0 && vehicles.length > 1) {
          const firstAvailableVehicle = vehicles.find(
            (v) => v.plate !== vehicleToDelete.plate,
          );
          if (firstAvailableVehicle) {
            dispatch(
              setCustomer({
                selectedVehicles: [firstAvailableVehicle.plate],
                pendingFilters: undefined,
                currentPlate: undefined,
              }),
            );
            return;
          }
        }

        if (vehicles.length <= 1) {
          dispatch(
            setCustomer({
              selectedVehicles: [],
              vehicles: [],
              pendingFilters: undefined,
              currentPlate: undefined,
              searchPlate: "",
            }),
          );
        } else {
          dispatch(
            setCustomer({
              selectedVehicles: newSelected,
              pendingFilters: undefined,
              currentPlate: undefined,
            }),
          );
        }
      }
    },
    [dispatch, vehicleToDelete, customer?.selectedVehicles, vehicles],
  );

  const requestDelete = useCallback(() => {
    if (isLoading) return;
    setIsLoading(true);
    setIsVisibleTryDeleteModal(false);
    dispatch(
      deleteVehicle({
        plate: vehicleToDelete?.plate ?? "",
        identifier:
          vehicleToDelete?.listActiveAccessPoint?.[0]?.identifierNumber ?? "",
        callback: handleDeleteVehicle,
      }),
    );
  }, [isLoading, vehicleToDelete, dispatch, handleDeleteVehicle]);

  const showTryDelete = useCallback((vehicle: Vehicle) => {
    setVehicleToDelete(vehicle);
    setIsVisibleTryDeleteModal(true);
  }, []);

  return {
    goBack,
    goToBuy,
    vehicles: filteredVehicles,
    isShowingMenu,
    toggleMenu,
    searchPlate,
    setSearchPlate,
    selectedVehicles: customer?.selectedVehicles || [],
    toggleVehicleSelection,
    toggleSelectAll,
    isAllSelected,
    isLoading: isLoading || customer?.isLoadingVehicles!,
    showTryDelete,
    isVisibleTryDeleteModal,
    setIsVisibleTryDeleteModal,
    requestDelete,
    error: error && !customer?.isLoadingVehicles && !isLoading,
    setError,
    vehicleToDelete,
  };
}
